import React, { useEffect, useState } from 'react';
import { FiMonitor, FiTablet, FiSmartphone, FiAlertTriangle, FiLogOut } from 'react-icons/fi';
import { useAuthContext } from '../auth-config';
import { useNavigate } from 'react-router-dom';

export const isMobileDevice = (): boolean => {
  if (typeof window === 'undefined') return false;

  // 1. Check physical hardware display dimensions (cannot be faked by "Desktop Mode")
  const minDimension = Math.min(window.screen.width, window.screen.height);
  const isLandscapeTabletOrDesktop = window.screen.width >= 1024 && window.screen.width > window.screen.height;
  const isSmallPhysicalScreen = minDimension < 600 && !isLandscapeTabletOrDesktop;

  // 2. Check touch capability and coarse pointer
  const isTouchDevice = 'ontouchstart' in window || navigator.maxTouchPoints > 0;
  const isCoarsePointer = window.matchMedia && window.matchMedia('(pointer: coarse)').matches;

  // 3. User agent fallback check
  const isMobileUserAgent = /Android|webOS|iPhone|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);

  return isSmallPhysicalScreen && (isTouchDevice || isCoarsePointer || isMobileUserAgent);
};

interface GuardProps {
  children: React.ReactNode;
}

export const MobileRestrictionGuard: React.FC<GuardProps> = ({ children }) => {
  const { isAuthenticated, signOut } = useAuthContext();
  const navigate = useNavigate();
  const [isMobile, setIsMobile] = useState<boolean>(false);
  const [isLoggingOut, setIsLoggingOut] = useState<boolean>(false);

  const handleLogout = async () => {
    try {
      setIsLoggingOut(true);
      await signOut();
      navigate('/login');
    } catch (error) {
      console.error('Logout failed:', error);
    } finally {
      setIsLoggingOut(false);
    }
  };

  useEffect(() => {
    const checkDevice = () => {
      setIsMobile(isMobileDevice());
    };

    checkDevice();
    window.addEventListener('resize', checkDevice);
    return () => window.removeEventListener('resize', checkDevice);
  }, []);

  if (isMobile && isAuthenticated) {
    return (
      <div className="min-h-screen w-full bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950 text-white flex flex-col items-center justify-center p-6 text-center select-none relative overflow-hidden">
        {/* Background Ambient Glows */}
        <div className="absolute top-1/4 left-1/2 -translate-x-1/2 -translate-y-1/2 w-96 h-96 bg-blue-600/15 rounded-full blur-3xl pointer-events-none" />
        <div className="absolute bottom-1/4 left-1/2 -translate-x-1/2 translate-y-1/2 w-80 h-80 bg-indigo-600/15 rounded-full blur-3xl pointer-events-none" />

        <div className="max-w-md w-full bg-slate-900/80 backdrop-blur-2xl border border-slate-800/80 rounded-3xl p-8 shadow-2xl space-y-6 relative overflow-hidden z-10">
          {/* Animated Gradient Top Border */}
          <div className="absolute top-0 left-0 right-0 h-1.5 bg-gradient-to-r from-blue-500 via-sky-400 via-indigo-500 to-amber-400 animate-pulse" />

          {/* Icon Badge with Pulse Effect */}
          <div className="relative w-20 h-20 mx-auto flex items-center justify-center">
            <div className="absolute inset-0 bg-blue-500/20 rounded-2xl animate-ping opacity-25" />
            <div className="w-20 h-20 bg-gradient-to-br from-blue-500/20 to-indigo-500/20 border border-blue-500/40 rounded-2xl flex items-center justify-center text-blue-400 text-3xl shadow-lg backdrop-blur-md">
              <FiAlertTriangle />
            </div>
          </div>

          <div className="space-y-2">
            <div className="inline-block px-3 py-1 bg-blue-500/10 border border-blue-500/30 rounded-full text-[11px] font-extrabold uppercase tracking-widest text-blue-400 mb-1">
              Distraction-Free Study Zone
            </div>
            <h1 className="text-2xl sm:text-3xl font-black tracking-tight text-white bg-clip-text text-transparent bg-gradient-to-r from-white via-slate-100 to-slate-300">
              Desktop or Tablet Required
            </h1>
            <p className="text-slate-400 text-sm font-medium leading-relaxed">
              Masti Manch is engineered specifically for dedicated learning on larger screens. Smartphone handsets are restricted to preserve study focus and interactive ergonomics.
            </p>
          </div>

          {/* Supported Devices Cards */}
          <div className="bg-slate-950/70 border border-slate-800/80 rounded-2xl p-5 space-y-3 shadow-inner">
            <span className="text-[10px] font-extrabold uppercase tracking-widest text-slate-400 block">Approved Study Platforms</span>
            <div className="grid grid-cols-2 gap-3 pt-1">
              <div className="flex flex-col items-center p-3 bg-slate-900/90 rounded-xl border border-slate-800 hover:border-blue-500/40 transition-colors group">
                <div className="p-2.5 bg-blue-500/10 rounded-lg text-blue-400 group-hover:scale-110 transition-transform">
                  <FiMonitor size={22} />
                </div>
                <span className="text-xs font-bold text-slate-200 mt-2">Desktop / Laptop</span>
                <span className="text-[10px] text-slate-500 font-medium">Windows, Mac, ChromeOS</span>
              </div>
              <div className="flex flex-col items-center p-3 bg-slate-900/90 rounded-xl border border-slate-800 hover:border-indigo-500/40 transition-colors group">
                <div className="p-2.5 bg-indigo-500/10 rounded-lg text-indigo-400 group-hover:scale-110 transition-transform">
                  <FiTablet size={22} />
                </div>
                <span className="text-xs font-bold text-slate-200 mt-2">Standard Tablet</span>
                <span className="text-[10px] text-slate-500 font-medium">iPad & Galaxy Tab</span>
              </div>
            </div>
          </div>

          <div className="pt-2 text-xs text-slate-400 font-mono flex items-center justify-center gap-1.5">
            <span>Open</span>
            <strong className="text-blue-400 bg-blue-500/10 px-2 py-0.5 rounded border border-blue-500/20">study.mastimanch.com</strong>
            <span>on your computer</span>
          </div>

          {/* Logout Button */}
          <div className="pt-4 border-t border-slate-800/80 flex justify-center">
            <button
              onClick={handleLogout}
              disabled={isLoggingOut}
              className="text-xs font-semibold text-slate-400 hover:text-red-400 transition-colors duration-200 flex items-center gap-1.5 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isLoggingOut ? (
                <>
                  <span className="w-3.5 h-3.5 border-2 border-slate-400 border-t-transparent rounded-full animate-spin"></span>
                  <span>Logging out...</span>
                </>
              ) : (
                <>
                  <FiLogOut size={14} />
                  <span>Logout</span>
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    );
  }

  return <>{children}</>;
};

export default MobileRestrictionGuard;
